import { IQueryClassDependency } from '@kurtosys/types/dependencies/IQueryClassDependency.js';
import { ITransformClassDependency } from '@kurtosys/types/dependencies/ITransformClassDependency.js';
import { TConditionalHelperDependencies } from '@kurtosys/types/helpers/TConditionalHelperDependencies.js';
import { IExecutionOptions, IQueryContext, IQueryOptions, TQueryType } from '@kurtosys/types/query/index.js';
import { IQueryOptionsOptions } from '@kurtosys/types/query/options/IQueryOptionsOptions.js';

import { commonUtils, helpers } from '../../common/index.js';
import { getQueryClass } from '../queryUtils/getQueryClass.js';
import { queryHelper } from '../queryUtils/index.js';

import { BaseQuery } from './base/BaseQuery.js';

export class DocumentEntityQuery extends BaseQuery<Omit<IQueryOptionsOptions, 'documentEntity'>> {
	constructor(
		public dependencies: IQueryClassDependency & ITransformClassDependency,
		public queryOptions: IQueryOptions,
		public translationHelper?: helpers.TranslationHelper,
	) {
		super(dependencies, queryOptions, translationHelper);
	}

	getOptions(): Omit<IQueryOptionsOptions, 'documentEntity'> {
		const { options = {} } = this.queryOptions;
		return options.documentEntity;
	}
	getRawValue<T = any>(executionOptions: IExecutionOptions): T {
		const context = queryHelper.getContext(executionOptions, this.queryOptions);
		if (context) {
			const contextConfiguration = queryHelper.getContextConfiguration(executionOptions, this.queryOptions);
			const { documentEntityKeys: documentEntityKeysConfig } = contextConfiguration;
			const { documentEntities, documentJoinMap, document } = context;
			const conditionalHelperDependencies: TConditionalHelperDependencies = {
				queryClass: this.dependencies.queryClass,
			};
			const conditionalHelper = new helpers.ConditionalHelper(
				conditionalHelperDependencies,
				{ conditions: [] },
				this.translationHelper,
			);
			if (documentEntities && Object.keys(documentEntities).length === 0) {
				return;
			}

			// document context programmatically loaded by the app
			const documentClientCode = document.clientCode;
			// join map loaded into context by the API
			const entityClientCodes = (documentJoinMap && documentJoinMap[documentClientCode]) || [];

			// Config keys that would contain the conditions to filter the entities (user defined category)
			const options = this.getOptions();
			const optionsKeys = Object.keys(options) as TQueryType[];
			if (optionsKeys && optionsKeys.length > 0) {
				const queryOptionsType = optionsKeys[0];
				// Building up IQueryOptions due to options nesting for different queryOptionTypes that can be use to Query
				const entityQueryOptions: IQueryOptions = {
					queryOptionsType,
					options,
				};
				let documentEntityKeyGenerated = false;
				if (!options[queryOptionsType].documentEntityKey) {
					options[queryOptionsType].documentEntityKey = commonUtils.generateGuid();
					documentEntityKeyGenerated = true;
				}
				const documentEntityKey = options[queryOptionsType].documentEntityKey;
				// Load the query to fetch the data from the entity
				const query = getQueryClass(this.dependencies, entityQueryOptions, this.translationHelper)
				if (!query) {
					return;
				}
				const documentEntityByKeys: IQueryContext['documentEntityByKeys'] = {};
				for (const clientCode of entityClientCodes) {
					const entity = documentEntities[clientCode];
					if (entity) {
						const documentEntityConfig =
							documentEntityKeysConfig && documentEntityKeysConfig[documentEntityKey];
						const hasConfig = typeof documentEntityConfig === 'object';

						if (!hasConfig && !documentEntityKeyGenerated) {
							console.warn(
								`Document Entity Query: documentEntityKey of "${documentEntityKey}" not found, using first document as fallback.`,
							);
						}

						const { conditional, entityType } = documentEntityConfig || {};
						let valid = !conditional && !entityType;
						if (valid && hasConfig && !documentEntityKeyGenerated) {
							console.warn(
								`Document Entity Query: documentEntityKey of "${documentEntityKey}" is found, but has no entityType or conditional defined. Using first document as fallback.`,
							);
						}
						if (!entityType || entityType === entity.type) {
							valid = true;
							if (conditional) {
								// Build up conditional execution options to allow the query options instead of the dot notation when configuring
								const conditionalExecutionOptions = {
									...executionOptions,
									context: {
										...executionOptions.context,
										entityByType: {
											[entity.type]: entity,
										},
									},
								};
								valid = conditionalHelper.matches(
									entity,
									documentEntityConfig.conditional,
									conditionalExecutionOptions,
								);
							}
						}
						if (valid) {
							documentEntityByKeys[documentEntityKey] = entity;
							break;
						}
					}
				}
				const workingExecutionOptions = {
					...executionOptions,
					context: { ...executionOptions.context, documentEntityByKeys },
				};
				// Fire off the option query using new context
				return query.execute(workingExecutionOptions);
			}
		}
		return;
	}
}
